import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "candidate-page candidate-results" }
const _hoisted_2 = { class: "candidate-container" }
const _hoisted_3 = { class: "candidate-page-head candidate-page-header" }
const _hoisted_4 = { class: "candidate-title-back" }
const _hoisted_5 = {
  key: 2,
  class: "results-layout"
}
const _hoisted_6 = { class: "result-session-field" }
const _hoisted_7 = {
  key: 0,
  class: "results-side-state"
}
const _hoisted_8 = {
  key: 1,
  class: "results-side-state is-error"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "attempt-item-top" }
const _hoisted_11 = {
  key: 3,
  class: "results-empty"
}
const _hoisted_12 = {
  key: 2,
  class: "result-detail"
}
const _hoisted_13 = { class: "result-score" }
const _hoisted_14 = { class: "result-copy" }
const _hoisted_15 = { class: "result-copy-grid" }
const _hoisted_16 = { class: "score-grid" }
const _hoisted_17 = { class: "score-item-top" }
const _hoisted_18 = { class: "certificate-copy" }
const _hoisted_19 = {
  key: 0,
  class: "certificate-error"
}
const _hoisted_20 = { class: "certificate-actions" }
const _hoisted_21 = {
  key: 1,
  class: "audio-detail-container"
}

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ElMessage } from 'element-plus'
import { $enum } from '@/global/index'
import CertificateDialog from '@/components/candidate/CertificateDialog.vue'
import AudioDetail from '@/views/audio/index.vue'
import { downloadPilotAssessmentCertificate } from '@/utils/downloadPilotAssessmentCertificate'
import {
  CandidateScrollbar,
  VkButton,
  VkCard,
  VkEmpty,
  VkSelect,
  VkTag,
} from '@/components/candidate/ui/index'
import {
  getPilotAssessmentCertificate,
  getPilotAssessmentResultDetail,
  getPilotAssessmentResultRoundList,
  getPilotAssessmentResultSessionOptions,
  getSimulatorTrainingDetail,
  getStationSceneDataByStationId,
  getTrainingAiDataById,
  PilotAssessmentCertificate,
  PilotAssessmentResultDetail,
  PilotAssessmentResultRound,
  PilotAssessmentResultSessionOption,
  PilotAssessmentResultSubject,
} from '@/service'

const TEMP_REPLAY_TRAINING_ID = '2079383015917137920'

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter()
const store = useStore()
const resultSessions = ref<PilotAssessmentResultSessionOption[]>([])
const rounds = ref<PilotAssessmentResultRound[]>([])
const resultDetail = ref<PilotAssessmentResultDetail | null>(null)
const selectedSessionId = ref('')
const selectedRoundId = ref('')
const sessionLoading = ref(true)
const roundLoading = ref(false)
const detailLoading = ref(false)
const certificateLoading = ref(false)
const sessionError = ref('')
const roundError = ref('')
const detailError = ref('')
const certificateError = ref('')
const certificateVisible = ref(false)
const certificateData = ref<PilotAssessmentCertificate | null>(null)
const certificateAction = ref<'view' | 'download'>('view')
const replayLoadingSubjectCode = ref('')
const audioDetailVisible = ref(false)
const audioTrainingId = ref('')
let sessionRequestSequence = 0
let roundRequestSequence = 0
let detailRequestSequence = 0

const sessionSelectOptions = computed(() =>
  resultSessions.value.map((session) => ({
    value: String(session.sessionId),
    label: `${session.sessionName} · ${session.completedRoundCount} 次 · 最高 ${
      formatOptionalScore(session.highestScore)
    }`,
  })),
)

const detailCertificateGenerated = computed(() =>
  certificateGenerated(resultDetail.value?.certificateStatus),
)

const scoreCircleStyle = computed(() => {
  const score = Number(resultDetail.value?.totalScore || 0)
  const percentage = Number.isFinite(score) ? Math.min(100, Math.max(0, score)) : 0
  return {
    '--score-progress-value': percentage,
    '--score-progress-opacity': percentage > 0 ? 1 : 0,
  }
})

async function loadSessionOptions() {
  const requestSequence = ++sessionRequestSequence
  sessionLoading.value = true
  sessionError.value = ''
  try {
    const response = await getPilotAssessmentResultSessionOptions()
    if (requestSequence !== sessionRequestSequence) return
    if (response.code != $enum.EnumResult.SUCCESS || !Array.isArray(response.data)) {
      throw new Error(response.message || '个人成绩加载失败')
    }
    resultSessions.value = response.data
    if (!resultSessions.value.length) {
      selectedSessionId.value = ''
      rounds.value = []
      resultDetail.value = null
      return
    }
    const selectionExists = resultSessions.value.some(
      (session) => String(session.sessionId) === selectedSessionId.value,
    )
    if (!selectionExists) {
      selectedSessionId.value = String(resultSessions.value[0].sessionId)
    }
    await loadRoundList()
  } catch (error) {
    if (requestSequence !== sessionRequestSequence) return
    resultSessions.value = []
    rounds.value = []
    resultDetail.value = null
    sessionError.value = error instanceof Error ? error.message : '个人成绩加载失败'
  } finally {
    if (requestSequence === sessionRequestSequence) sessionLoading.value = false
  }
}

async function loadRoundList() {
  const requestSequence = ++roundRequestSequence
  detailRequestSequence += 1
  const sessionId = selectedSessionId.value
  rounds.value = []
  resultDetail.value = null
  detailLoading.value = false
  selectedRoundId.value = ''
  roundError.value = ''
  detailError.value = ''
  certificateError.value = ''
  certificateVisible.value = false
  certificateData.value = null
  if (!sessionId) return
  roundLoading.value = true
  try {
    const response = await getPilotAssessmentResultRoundList({ sessionId })
    if (requestSequence !== roundRequestSequence) return
    if (response.code != $enum.EnumResult.SUCCESS || !Array.isArray(response.data)) {
      throw new Error(response.message || '考试轮次加载失败')
    }
    rounds.value = response.data
    if (rounds.value.length) {
      selectedRoundId.value = String(rounds.value[0].roundId)
      await loadResultDetail()
    }
  } catch (error) {
    if (requestSequence !== roundRequestSequence) return
    roundError.value = error instanceof Error ? error.message : '考试轮次加载失败'
  } finally {
    if (requestSequence === roundRequestSequence) roundLoading.value = false
  }
}

async function loadResultDetail() {
  const requestSequence = ++detailRequestSequence
  const roundId = selectedRoundId.value
  resultDetail.value = null
  detailError.value = ''
  certificateError.value = ''
  certificateVisible.value = false
  certificateData.value = null
  if (!roundId) return
  detailLoading.value = true
  try {
    const response = await getPilotAssessmentResultDetail({ roundId })
    if (requestSequence !== detailRequestSequence) return
    if (response.code != $enum.EnumResult.SUCCESS || !response.data) {
      throw new Error(response.message || '成绩详情加载失败')
    }
    resultDetail.value = {
      ...response.data,
      subjects: Array.isArray(response.data.subjects) ? response.data.subjects : [],
    }
  } catch (error) {
    if (requestSequence !== detailRequestSequence) return
    detailError.value = error instanceof Error ? error.message : '成绩详情加载失败'
  } finally {
    if (requestSequence === detailRequestSequence) detailLoading.value = false
  }
}

function handleSessionChange() {
  loadRoundList()
}

function selectRound(attempt: PilotAssessmentResultRound) {
  const roundId = String(attempt.roundId)
  if (roundId === selectedRoundId.value || detailLoading.value) return
  selectedRoundId.value = roundId
  loadResultDetail()
}

async function openCertificate(action: 'view' | 'download') {
  if (!detailCertificateGenerated.value || !selectedRoundId.value || certificateLoading.value) {
    return
  }
  certificateAction.value = action
  certificateLoading.value = true
  certificateError.value = ''
  try {
    const response = await getPilotAssessmentCertificate({
      roundId: selectedRoundId.value,
    })
    if (response.code != $enum.EnumResult.SUCCESS || !response.data) {
      throw new Error(response.message || '证书加载失败')
    }
    certificateData.value = response.data
    if (action === 'download') {
      downloadPilotAssessmentCertificate(response.data)
      certificateVisible.value = false
    } else {
      certificateVisible.value = true
    }
  } catch (error) {
    certificateError.value = error instanceof Error ? error.message : '证书加载失败'
  } finally {
    certificateLoading.value = false
  }
}

function certificateGenerated(status?: string): boolean {
  return String(status || '').trim().toUpperCase() === 'GENERATED'
}

function subjectPassed(status: string | number): boolean {
  return status === 3 || status === '3' || String(status || '').trim().toUpperCase() === 'PASSED'
}

function buildReplayDeviationInfo(itemList: any[] = []) {
  const deviationTypeMap: Record<string, string> = {
    高度偏差: 'height',
    位置偏差: 'position',
    夹角偏差: 'headAngle',
    角速度偏差: 'angle',
    速度偏差: 'speed',
  }
  return itemList.reduce((result: Record<string, string>, item: any) => {
    const key = deviationTypeMap[item?.typeName]
    if (key) result[key] = item.typeName
    return result
  }, {})
}

async function handleSubjectAction(subject: PilotAssessmentResultSubject) {
  if (subject.subjectCode !== 'BASE_FLIGHT') {
    openAudioDetail(subject)
    return
  }
  if (replayLoadingSubjectCode.value) return
  window?.playClick()
  replayLoadingSubjectCode.value = subject.subjectCode
  try {
    // 后端字段上线前使用临时训练记录，字段返回后自动使用实际 trainingId。
    const trainingId = String(subject.trainingId || TEMP_REPLAY_TRAINING_ID)
    const trainingResponse = (await getSimulatorTrainingDetail({ id: trainingId })) as any
    if (trainingResponse.code != $enum.EnumResult.SUCCESS || !trainingResponse.data) {
      throw new Error(trainingResponse.message || '飞行成绩详情加载失败')
    }

    const trainingData = trainingResponse.data
    if (!trainingData.stationId) {
      throw new Error('飞行成绩缺少场景站点信息')
    }
    const [sceneResponse, aiResponse] = (await Promise.all([
      getStationSceneDataByStationId({ stationId: trainingData.stationId }),
      getTrainingAiDataById({ id: trainingId }),
    ])) as any[]
    if (
      sceneResponse.code != $enum.EnumResult.SUCCESS ||
      !sceneResponse.data ||
      !Object.keys(sceneResponse.data).length
    ) {
      throw new Error(sceneResponse.message || '飞行场景数据加载失败')
    }
    if (aiResponse.code != $enum.EnumResult.SUCCESS || !aiResponse.data) {
      throw new Error(aiResponse.message || '飞行AI评价加载失败')
    }

    const aiData = aiResponse.data
    const abilityData = aiData.radar
      ? {
          flight_stability: aiData.flightStability,
          deviation_control: aiData.deviationControl,
          error_correction: aiData.errorCorrection,
          anti_disturbance: aiData.antiDisturbance,
          stick_proficiency: aiData.stickProficiency,
        }
      : null

    store.commit('persistence/setStationId', trainingData.stationId)
    store.commit('viewData/setFlyReplayData', {
      title: '全科',
      score: trainingData.overallScore,
      isPass: trainingData.isPass,
      rating: aiData.rating,
      abilityData,
      flightSummary: aiData.flightConclusion,
      trainingSuggestion: aiData.trainingSuggestion,
      windDirection: trainingData.windDirection,
      windSpeed: trainingData.windPower,
      pageMode: 2,
      enterMode: 2,
      trainingId,
      deviationInfo: buildReplayDeviationInfo(trainingData.itemList),
      scenePictureUrl: sceneResponse.data.scenePictureUrl,
      uavNestId: trainingData.uavNestId,
      uavSpecificationId: trainingData.uavSpecificationId,
      enterPageMode: 2,
    })
    store.commit('viewData/setShowFlyReplay', true)
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '回放数据加载失败，请重试')
  } finally {
    replayLoadingSubjectCode.value = ''
  }
}

function openAudioDetail(subject: PilotAssessmentResultSubject) {
  const trainingId = String(subject.trainingId || '')
  if (!trainingId) {
    ElMessage.info('当前科目暂无结果数据')
    return
  }
  window?.playClick()
  audioTrainingId.value = trainingId
  audioDetailVisible.value = true
}

function closeAudioDetail() {
  audioDetailVisible.value = false
  audioTrainingId.value = ''
}

function formatScore(score: number | string): string {
  const value = Number(score)
  return Number.isFinite(value) ? value.toFixed(1) : String(score)
}

function formatOptionalScore(score: number | string | null | undefined): string {
  if (score === null || score === undefined || score === '') return '--'
  return formatScore(score)
}

function goHall() {
  router.push('/pilot-assesment')
}

onMounted(() => {
  loadSessionOptions()
})

onBeforeUnmount(() => {
  closeAudioDetail()
  sessionRequestSequence += 1
  roundRequestSequence += 1
  detailRequestSequence += 1
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(VkButton), {
            class: "candidate-back-button",
            variant: "text",
            tone: "default",
            onClick: goHall
          }, {
            default: _withCtx(() => _cache[4] || (_cache[4] = [
              _createTextVNode(" ← 返回考试大厅 ")
            ])),
            _: 1
          }),
          _cache[5] || (_cache[5] = _createElementVNode("div", null, [
            _createElementVNode("h1", null, "个人成绩"),
            _createElementVNode("p", null, "按考试场次和考试轮次查看成绩与专业技能证书。")
          ], -1))
        ])
      ]),
      (sessionLoading.value)
        ? (_openBlock(), _createBlock(_unref(VkCard), {
            key: 0,
            class: "candidate-card candidate-empty results-empty-card"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VkEmpty), {
                size: "large",
                description: "个人成绩加载中..."
              })
            ]),
            _: 1
          }))
        : (sessionError.value)
          ? (_openBlock(), _createBlock(_unref(VkCard), {
              key: 1,
              class: "candidate-card candidate-empty results-empty-card"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(VkEmpty), {
                  size: "large",
                  description: sessionError.value
                }, null, 8, ["description"])
              ]),
              _: 1
            }))
          : (resultSessions.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(_unref(VkCard), { class: "candidate-card results-side" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_6, [
                      _cache[6] || (_cache[6] = _createElementVNode("label", null, "考试场次", -1)),
                      _createVNode(_unref(VkSelect), {
                        modelValue: selectedSessionId.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedSessionId).value = $event)),
                        class: "result-session-select",
                        options: sessionSelectOptions.value,
                        placeholder: "请选择考试场次",
                        "popper-class": "candidate-results-session-popper",
                        onChange: handleSessionChange
                      }, null, 8, ["modelValue", "options"])
                    ]),
                    _cache[8] || (_cache[8] = _createElementVNode("h3", null, "考试轮次", -1)),
                    (roundLoading.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, "轮次加载中..."))
                      : (roundError.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createElementVNode("span", null, _toDisplayString(roundError.value), 1),
                            _createVNode(_unref(VkButton), {
                              size: "small",
                              variant: "text",
                              tone: "primary",
                              onClick: loadRoundList
                            }, {
                              default: _withCtx(() => _cache[7] || (_cache[7] = [
                                _createTextVNode(" 重试 ")
                              ])),
                              _: 1
                            })
                          ]))
                        : (rounds.value.length)
                          ? (_openBlock(), _createBlock(_unref(CandidateScrollbar), {
                              key: 2,
                              class: "attempt-list",
                              height: "355px"
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rounds.value, (attempt) => {
                                  return (_openBlock(), _createElementBlock("button", {
                                    key: String(attempt.roundId),
                                    class: _normalizeClass(["attempt-item", { active: selectedRoundId.value === String(attempt.roundId) }]),
                                    onClick: ($event: any) => (selectRound(attempt))
                                  }, [
                                    _createElementVNode("div", _hoisted_10, [
                                      _createElementVNode("b", null, "第 " + _toDisplayString(attempt.roundNo) + " 次考试", 1),
                                      _createElementVNode("span", null, [
                                        _createVNode(_unref(VkTag), {
                                          size: "small",
                                          theme: certificateGenerated(attempt.certificateStatus) ? 'success' : 'danger',
                                          variant: "light",
                                          shape: "round"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(certificateGenerated(attempt.certificateStatus) ? '已生成证书' : '未生成'), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["theme"])
                                      ])
                                    ]),
                                    _createElementVNode("small", null, _toDisplayString(attempt.completeTime) + " · 总分 " + _toDisplayString(formatScore(attempt.totalScore)), 1)
                                  ], 10, _hoisted_9))
                                }), 128))
                              ]),
                              _: 1
                            }))
                          : (_openBlock(), _createElementBlock("div", _hoisted_11, "暂无已完成轮次"))
                  ]),
                  _: 1
                }),
                (detailLoading.value)
                  ? (_openBlock(), _createBlock(_unref(VkCard), {
                      key: 0,
                      class: "candidate-card candidate-empty results-empty-card"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(VkEmpty), {
                          size: "large",
                          description: "成绩详情加载中..."
                        })
                      ]),
                      _: 1
                    }))
                  : (detailError.value)
                    ? (_openBlock(), _createBlock(_unref(VkCard), {
                        key: 1,
                        class: "candidate-card candidate-empty results-empty-card"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(VkEmpty), {
                            size: "large",
                            description: detailError.value
                          }, null, 8, ["description"])
                        ]),
                        _: 1
                      }))
                    : (resultDetail.value)
                      ? (_openBlock(), _createElementBlock("section", _hoisted_12, [
                          _createVNode(_unref(VkCard), { class: "candidate-card result-hero" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_13, [
                                _createElementVNode("div", {
                                  class: "score-circle",
                                  style: _normalizeStyle(scoreCircleStyle.value)
                                }, [
                                  _cache[10] || (_cache[10] = _createElementVNode("svg", {
                                    class: "score-circle__chart",
                                    viewBox: "0 0 160 160",
                                    "aria-hidden": "true"
                                  }, [
                                    _createElementVNode("circle", {
                                      class: "score-circle__track",
                                      cx: "80",
                                      cy: "80",
                                      r: "73.5",
                                      pathLength: "100"
                                    }),
                                    _createElementVNode("circle", {
                                      class: "score-circle__value",
                                      cx: "80",
                                      cy: "80",
                                      r: "73.5",
                                      pathLength: "100"
                                    })
                                  ], -1)),
                                  _createElementVNode("div", null, [
                                    _createElementVNode("strong", null, _toDisplayString(formatScore(resultDetail.value.totalScore)), 1),
                                    _cache[9] || (_cache[9] = _createElementVNode("span", null, "本轮总分", -1))
                                  ])
                                ], 4)
                              ]),
                              _createElementVNode("div", _hoisted_14, [
                                _createElementVNode("h2", null, _toDisplayString(resultDetail.value.sessionName) + " · 第 " + _toDisplayString(resultDetail.value.roundNo) + " 次考试", 1),
                                _cache[14] || (_cache[14] = _createElementVNode("p", null, "按本场次启用科目和权重计算本轮成绩。", -1)),
                                _createElementVNode("div", _hoisted_15, [
                                  _createElementVNode("div", null, [
                                    _cache[11] || (_cache[11] = _createElementVNode("span", null, "历史最高得分", -1)),
                                    _createElementVNode("b", null, _toDisplayString(formatOptionalScore(resultDetail.value.highestScore)), 1)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[12] || (_cache[12] = _createElementVNode("span", null, "考试完成时间", -1)),
                                    _createElementVNode("b", null, _toDisplayString(resultDetail.value.completeTime), 1)
                                  ]),
                                  _createElementVNode("div", null, [
                                    _cache[13] || (_cache[13] = _createElementVNode("span", null, "专业技能证书", -1)),
                                    _createElementVNode("b", null, _toDisplayString(detailCertificateGenerated.value ? '已生成' : '未生成'), 1)
                                  ])
                                ])
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_unref(VkCard), { class: "candidate-card score-card" }, {
                            default: _withCtx(() => [
                              _cache[15] || (_cache[15] = _createElementVNode("div", { class: "candidate-section-title" }, [
                                _createElementVNode("div", null, [
                                  _createElementVNode("h3", null, "科目成绩"),
                                  _createElementVNode("p", null, "仅展示当前场次已启用科目。")
                                ])
                              ], -1)),
                              _createElementVNode("div", _hoisted_16, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resultDetail.value.subjects, (subject) => {
                                  return (_openBlock(), _createElementBlock("article", {
                                    key: subject.subjectCode,
                                    class: "score-item"
                                  }, [
                                    _createElementVNode("div", _hoisted_17, [
                                      _createElementVNode("h3", null, _toDisplayString(subject.subjectName), 1),
                                      _createVNode(_unref(VkTag), {
                                        size: "small",
                                        theme: subjectPassed(subject.subjectStatus) ? 'success' : 'danger',
                                        variant: "light",
                                        shape: "round"
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(subjectPassed(subject.subjectStatus) ? '通过' : '未通过'), 1)
                                        ]),
                                        _: 2
                                      }, 1032, ["theme"])
                                    ]),
                                    _createElementVNode("strong", null, _toDisplayString(formatScore(subject.score)), 1),
                                    _createVNode(_unref(VkButton), {
                                      tone: "secondary",
                                      class: "score-item-action",
                                      loading: 
                    subject.subjectCode === 'BASE_FLIGHT' &&
                    replayLoadingSubjectCode.value === subject.subjectCode
                  ,
                                      onClick: ($event: any) => (handleSubjectAction(subject))
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(subject.subjectCode === 'BASE_FLIGHT' ? '回放' : '查看结果'), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["loading", "onClick"])
                                  ]))
                                }), 128))
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_unref(VkCard), {
                            class: _normalizeClass(["candidate-card certificate-card", { 'is-muted': !detailCertificateGenerated.value }])
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_18, [
                                _createElementVNode("h3", null, _toDisplayString(detailCertificateGenerated.value
                    ? '专业技能证书已生成'
                    : '未达到证书生成成绩'), 1),
                                _createElementVNode("p", null, _toDisplayString(detailCertificateGenerated.value
                    ? '本轮所有启用科目和总分均达到通过要求，可查看并下载本轮专业技能证书。'
                    : '本轮存在未通过科目或本轮总分未达到通过要求，因此未生成证书。'), 1),
                                (certificateError.value)
                                  ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(certificateError.value), 1))
                                  : _createCommentVNode("", true)
                              ]),
                              _createElementVNode("div", _hoisted_20, [
                                _createVNode(_unref(VkButton), {
                                  variant: "outline",
                                  tone: "default",
                                  disabled: !detailCertificateGenerated.value || certificateLoading.value,
                                  loading: certificateLoading.value && certificateAction.value === 'view',
                                  onClick: _cache[1] || (_cache[1] = ($event: any) => (openCertificate('view')))
                                }, {
                                  default: _withCtx(() => _cache[16] || (_cache[16] = [
                                    _createTextVNode(" 查看证书 ")
                                  ])),
                                  _: 1
                                }, 8, ["disabled", "loading"]),
                                _createVNode(_unref(VkButton), {
                                  variant: "solid",
                                  tone: "default",
                                  disabled: !detailCertificateGenerated.value || certificateLoading.value,
                                  loading: certificateLoading.value && certificateAction.value === 'download',
                                  onClick: _cache[2] || (_cache[2] = ($event: any) => (openCertificate('download')))
                                }, {
                                  default: _withCtx(() => _cache[17] || (_cache[17] = [
                                    _createTextVNode(" 下载图片 ")
                                  ])),
                                  _: 1
                                }, 8, ["disabled", "loading"])
                              ])
                            ]),
                            _: 1
                          }, 8, ["class"])
                        ]))
                      : (_openBlock(), _createBlock(_unref(VkCard), {
                          key: 3,
                          class: "candidate-card results-empty-card"
                        }, {
                          default: _withCtx(() => _cache[18] || (_cache[18] = [
                            _createElementVNode("div", { class: "results-empty" }, "当前场次暂无已完成成绩", -1)
                          ])),
                          _: 1
                        }))
              ]))
            : (_openBlock(), _createBlock(_unref(VkCard), {
                key: 3,
                class: "candidate-card results-empty-card"
              }, {
                default: _withCtx(() => _cache[19] || (_cache[19] = [
                  _createElementVNode("div", { class: "results-empty" }, "暂无个人成绩", -1)
                ])),
                _: 1
              }))
    ]),
    (certificateData.value)
      ? (_openBlock(), _createBlock(CertificateDialog, {
          key: 0,
          modelValue: certificateVisible.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((certificateVisible).value = $event)),
          certificate: certificateData.value
        }, null, 8, ["modelValue", "certificate"]))
      : _createCommentVNode("", true),
    (audioDetailVisible.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
          (_openBlock(), _createBlock(AudioDetail, {
            key: audioTrainingId.value,
            "train-id": audioTrainingId.value,
            onOnBack: closeAudioDetail
          }, null, 8, ["train-id"]))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})